import React from 'react';
import { Pause, Play } from 'lucide-react';
import { PlacedBuilding } from '../../../hooks/analysis/useRoutes';
import { PersonRoute, timeOf } from '../utils/routes';
import { Replay, SelectedStop } from './RoutesMap';

export interface VisibleRoute extends PersonRoute {
    color: string;
    /** Resolved from the UID for display only */
    name: string;
    hidden: boolean;
}

interface RouteStopListProps {
    routes: VisibleRoute[];
    placedBuildings: Map<string, PlacedBuilding>;
    serviceLabel: (serviceKey: string) => string;
    selectedStop: SelectedStop | null;
    onSelectStop: (stop: SelectedStop) => void;
    replay: Replay | null;
    /** Plays the person's day on the map, or pauses it while playing */
    onTogglePlay: (uid: string) => void;
}

const formatGap = (minutes: number): string => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return rest ? `${hours} h ${rest} min` : `${hours} h`;
};

/** Each shown person's stops in order; a click pans the map to the stop */
const RouteStopList: React.FC<RouteStopListProps> = ({ routes, placedBuildings, serviceLabel, selectedStop, onSelectStop, replay, onTogglePlay }) => (
    <div className="routes-panel">
        {routes.length === 0 && <p className="text-muted">Välj en person ovanför kartan.</p>}
        {routes.map(route => {
            const replayed = replay?.uid === route.uid ? replay : null;
            const playing = Boolean(replayed?.running);
            return (
            <section key={route.uid} className="routes-person">
                <h3 className="routes-person-name">
                    <span className="routes-swatch" style={{ backgroundColor: route.color }} />
                    {route.name}
                    <span className="routes-person-count">{route.stops.length} stopp</span>
                    <button
                        type="button"
                        className="routes-play"
                        aria-label={playing ? `Pausa dagen för ${route.name}` : `Spela upp dagen för ${route.name}`}
                        aria-pressed={playing}
                        onClick={() => onTogglePlay(route.uid)}
                    >
                        {playing ? <Pause size={16} /> : <Play size={16} />}
                    </button>
                </h3>
                <ol className="routes-stops">
                    {route.stops.map((stop, index) => {
                        const selected = selectedStop?.uid === route.uid && selectedStop.index === index;
                        const current = replayed?.frame.atStop === index;
                        const ahead = replayed ? index >= replayed.frame.reachedStops : false;
                        const start = timeOf(stop.start);
                        const end = timeOf(stop.end);
                        return (
                            <li key={stop.buildingKey}>
                                {stop.minutesSincePrevious !== undefined && (
                                    <div className="routes-gap">{formatGap(stop.minutesSincePrevious)} sedan förra stoppet</div>
                                )}
                                <button
                                    type="button"
                                    className={`routes-stop ${selected ? 'selected' : ''} ${current ? 'current' : ''} ${ahead ? 'ahead' : ''}`}
                                    aria-current={current ? 'step' : undefined}
                                    onClick={() => onSelectStop({ uid: route.uid, index })}
                                >
                                    <span className="routes-stop-number" style={{ backgroundColor: route.color }}>{index + 1}</span>
                                    <span className="routes-stop-body">
                                        <span className="routes-stop-title">
                                            {placedBuildings.get(stop.buildingKey)?.name ?? stop.buildingKey}
                                        </span>
                                        <span className="routes-stop-time">{start === end ? start : `${start}–${end}`}</span>
                                        <span className="routes-stop-services">{stop.services.map(serviceLabel).join(', ')}</span>
                                    </span>
                                </button>
                            </li>
                        );
                    })}
                </ol>
            </section>
            );
        })}
    </div>
);

export default RouteStopList;
